import type { ReactNode } from 'react'
import clsx from 'clsx'
import { CountBadge } from '../Badge/Badge'
import styles from './Tabs.module.scss'

export interface TabItem<T extends string = string> {
  value: T
  label: ReactNode
  count?: number | string
  icon?: ReactNode
}

export interface TabsProps<T extends string> {
  items: TabItem<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  'aria-label'?: string
}

export function Tabs<T extends string>({ items, value, onChange, className, ...rest }: TabsProps<T>) {
  return (
    <div role="tablist" className={clsx(styles.tabs, className)} aria-label={rest['aria-label']}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={item.value === value}
          className={clsx(styles.tab, item.value === value && styles.active)}
          onClick={() => onChange(item.value)}
        >
          {item.icon}
          {item.label}
          {item.count !== undefined && <CountBadge value={item.count} />}
        </button>
      ))}
    </div>
  )
}

export function SegmentedControl<T extends string>({ items, value, onChange, className, ...rest }: TabsProps<T>) {
  return (
    <div role="radiogroup" className={clsx(styles.segmented, className)} aria-label={rest['aria-label']}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="radio"
          aria-checked={item.value === value}
          className={clsx(styles.segment, item.value === value && styles.segmentActive)}
          onClick={() => onChange(item.value)}
        >
          {item.icon}
          {item.label}
          {item.count !== undefined && <CountBadge value={item.count} />}
        </button>
      ))}
    </div>
  )
}

export interface ChipProps {
  active?: boolean
  onClick?: () => void
  icon?: ReactNode
  children: ReactNode
  className?: string
  title?: string
}

/** Toggleable filter chip ("On hold", "Repurpose", content types...). */
export function Chip({ active, onClick, icon, children, className, title }: ChipProps) {
  return (
    <button
      type="button"
      className={clsx(styles.chip, active && styles.chipActive, className)}
      onClick={onClick}
      aria-pressed={active}
      title={title}
    >
      {icon}
      {children}
    </button>
  )
}
